import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { useMemo, useRef, useState } from "react";

import {
  CommercialPlanSelector,
  CommercialPlanSelectorModal,
  getCommercialPlanSelection,
} from "@/components/commercial-plan-selector";
import {
  CommercialAllowanceKind,
  CommercialMeterKey,
  CommercialPendingPlanChangeStatus,
  CommercialPlanChangeStatus,
  CommercialPlanChangeTiming,
  CommercialPlanKey,
  formatCommercialPrice,
  type CommercialAllowanceComparison,
  type CommercialCatalogSnapshot,
  type CommercialExtraUsageBlockDefinition,
  type CommercialPlanChangePreview,
  type CommercialPlanChangeResult,
  type CommercialPlanDefinition,
  type CommercialPlanSnapshot,
} from "@/modules/commercial-plans";
import { uuidModule } from "@/modules/uuid";
import {
  MDSBadge,
  MDSBadgeVariant,
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSCheckbox,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalDialog,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { CommercialUsageProgress } from "./commercial-usage-progress";

interface CommercialPlanManagementProps {
  catalog?: CommercialCatalogSnapshot | null;
  initialSelectedPlanKey?: CommercialPlanKey | null;
  snapshot: CommercialPlanSnapshot;
  isChangingPlan?: boolean;
  onManageBilling?: () => void;
  onApplyPlanChange?: (args: {
    idempotencyKey: string;
    preview: CommercialPlanChangePreview;
  }) => Promise<CommercialPlanChangeResult>;
  onPreviewPlanChange?: (args: {
    paidExtraUsageBlocks: number;
    planKey: CommercialPlanKey;
  }) => Promise<CommercialPlanChangePreview>;
  onStartSubscription?: (args: {
    paidExtraUsageBlocks: number;
    planKey: CommercialPlanKey;
  }) => void;
}

const getCatalogPlan = (
  catalog: CommercialCatalogSnapshot | null | undefined,
  key: string | null | undefined
): CommercialPlanDefinition | undefined => catalog?.plans.find(plan => plan.key === key);

const getInitialPaidExtraUsageBlocks = (snapshot: CommercialPlanSnapshot): number => {
  if (snapshot.currentPlan.key === CommercialPlanKey.MemPro20x) {
    return snapshot.blockPolicy?.paidBlocks ?? 0;
  }

  return snapshot.migration?.recommendedPlanKey === CommercialPlanKey.MemPro20x
    ? (snapshot.migration.recommendedPaidExtraUsageBlocks ?? 0)
    : 0;
};

const formatDate = (value: string): string => {
  const date = DateTime.fromISO(value);
  if (!date.isValid) {
    return "your next renewal";
  }

  return date.toFormat("LLLL d, yyyy");
};

const formatCurrentPlanPrice = (monthlyPriceCents: number | null): string =>
  monthlyPriceCents === null
    ? "Provider price unavailable"
    : `${formatCommercialPrice(monthlyPriceCents)} / month`;

const formatAllowance = ({ key, value }: { key: CommercialMeterKey; value: number }): string => {
  if (key === CommercialMeterKey.VoiceModeHours) {
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)} hours`;
  }

  return new Intl.NumberFormat().format(value);
};

const PLAN_ALLOWANCE_HIGHLIGHTS: readonly {
  key: CommercialMeterKey;
  label: string;
}[] = [
  { key: CommercialMeterKey.UserRequests, label: "Agent requests" },
  { key: CommercialMeterKey.NewNotes, label: "new notes" },
  { key: CommercialMeterKey.ActiveNotes, label: "active notes" },
  { key: CommercialMeterKey.OpenTasksProjects, label: "open tasks + projects" },
  { key: CommercialMeterKey.ActiveCustomRoutines, label: "custom routines" },
  { key: CommercialMeterKey.RoutineExecutions, label: "routine executions" },
  { key: CommercialMeterKey.ActiveConnections, label: "connections" },
  { key: CommercialMeterKey.VoiceModeHours, label: "Voice Mode" },
] as const;

const getMeterLabel = (key: CommercialMeterKey): string =>
  PLAN_ALLOWANCE_HIGHLIGHTS.find(highlight => highlight.key === key)?.label ?? key;

const ExtraUsageDetails = ({
  block,
  canSelectBlocks,
  onDecrement,
  onIncrement,
  selectedPaidBlocks,
}: {
  block: CommercialExtraUsageBlockDefinition;
  canSelectBlocks: boolean;
  onDecrement: () => void;
  onIncrement: () => void;
  selectedPaidBlocks: number;
}) => (
  <ExtraUsagePanel>
    <ExtraUsageHeader>
      <div>
        <MDSText variant={MDSTextVariant.HeaderS} weight={MDSTextWeight.SemiBold}>
          Extra usage
        </MDSText>
        <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
          One transparent block, billed only after you authorize it.
        </MDSText>
      </div>
      <MDSBadge
        label={`+${formatCommercialPrice(block.monthlyPriceCents)} per billing period`}
        variant={MDSBadgeVariant.Emphasis}
      />
    </ExtraUsageHeader>
    <ExtraUsageGrid>
      {PLAN_ALLOWANCE_HIGHLIGHTS.map(({ key, label }) => (
        <MDSText key={key} variant={MDSTextVariant.TextS}>
          +{formatAllowance({ key, value: block.allowances[key] })} {label}
        </MDSText>
      ))}
    </ExtraUsageGrid>
    {canSelectBlocks ? (
      <ExtraUsageSelector aria-label="Paid Extra Usage blocks">
        <div>
          <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
            Paid blocks on this plan
          </MDSText>
          <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
            Choose an absolute number. The review shows the exact recurring total before billing.
          </MDSText>
        </div>
        <MDSButtonGroup>
          <MDSButton
            aria-label="Remove one Extra Usage block"
            isDisabled={selectedPaidBlocks === 0}
            onClick={onDecrement}
          >
            −
          </MDSButton>
          <ExtraUsageBlockCount aria-live="polite">{selectedPaidBlocks}</ExtraUsageBlockCount>
          <MDSButton aria-label="Add one Extra Usage block" onClick={onIncrement}>
            +
          </MDSButton>
        </MDSButtonGroup>
      </ExtraUsageSelector>
    ) : null}
    <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
      Extra blocks cover usage beyond Mem Pro 20×. Nothing is charged automatically; you choose the
      exact recurring block count and review the total before confirming.
    </MDSText>
  </ExtraUsagePanel>
);

const formatCurrentAllowance = (comparison: CommercialAllowanceComparison): string => {
  if (comparison.currentKind === CommercialAllowanceKind.Unlimited) {
    return "Unlimited";
  }

  if (
    comparison.currentKind === CommercialAllowanceKind.Unknown ||
    comparison.currentAmount === null
  ) {
    return "Provider-defined";
  }

  return formatAllowance({
    key: comparison.meterKey,
    value: comparison.currentAmount,
  });
};

const getPlanChangeActionLabel = (preview: CommercialPlanChangePreview): string => {
  if (preview.endsTrial) {
    return "End trial & upgrade";
  }

  if (preview.timing === CommercialPlanChangeTiming.Immediate) {
    return preview.amountDueNowCents > 0 ? "Pay and change plan" : "Change plan now";
  }

  if (preview.timing === CommercialPlanChangeTiming.Renewal) {
    return "Schedule at renewal";
  }

  return "No changes";
};

const PlanChangeDialog = ({
  errorMessage,
  isApplying,
  isLoadingPreview,
  isOpen,
  onApply,
  onClose,
  preview,
  selectedPlan,
  snapshot,
}: {
  errorMessage: string | null;
  isApplying: boolean;
  isLoadingPreview: boolean;
  isOpen: boolean;
  onApply?: () => void;
  onClose: () => void;
  preview: CommercialPlanChangePreview | null;
  selectedPlan: CommercialPlanDefinition;
  snapshot: CommercialPlanSnapshot;
}) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const canonicalMeterKeys = Object.values(CommercialMeterKey);
  const previewMeterKeys = new Set(
    preview?.allowanceComparisons.map(comparison => comparison.meterKey) ?? []
  );
  const previewIsComplete =
    preview !== null &&
    preview.targetPlanKey === selectedPlan.key &&
    preview.allowanceComparisons.length === canonicalMeterKeys.length &&
    previewMeterKeys.size === canonicalMeterKeys.length;
  const requiresOneWayAcknowledgement = preview?.oneWayLegacyExit === true;
  const canApply =
    onApply !== undefined &&
    previewIsComplete &&
    preview.timing !== CommercialPlanChangeTiming.Unchanged &&
    (!requiresOneWayAcknowledgement || acknowledged);
  const closeDialog = () => {
    setAcknowledged(false);
    onClose();
  };

  return (
    <MDSModalDialog isOpen={isOpen} isDismissable={!isApplying} handleClose={closeDialog}>
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        Review your plan change
      </MDSText>
      <MigrationDialogContent slot={MDSDialogSlotName.Content}>
        {errorMessage ? (
          <PlanChangeError role="alert">
            <MDSText variant={MDSTextVariant.ParagraphS}>{errorMessage}</MDSText>
          </PlanChangeError>
        ) : null}

        {isLoadingPreview ? (
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Loading the current billing preview…
          </MDSText>
        ) : preview ? (
          <>
            {preview.endsTrial ? (
              <MDSText variant={MDSTextVariant.ParagraphS}>
                Your free trial ends today when payment succeeds. Your paid plan and a fresh usage
                period start immediately. Any scheduled cancellation will be removed.
                {preview.nextBillingAt
                  ? ` Your next monthly payment is on ${formatDate(preview.nextBillingAt)}.`
                  : ""}
              </MDSText>
            ) : null}
            <MDSText variant={MDSTextVariant.ParagraphS}>
              {preview.timing === CommercialPlanChangeTiming.Immediate
                ? "This change takes effect after Stripe confirms any payment due."
                : preview.timing === CommercialPlanChangeTiming.Renewal
                  ? `This change is scheduled for ${formatDate(preview.effectiveAt)}.`
                  : "This matches your current plan configuration."}
              {preview.oneWayLegacyExit
                ? " You cannot return to the retired legacy price or allowances after confirming."
                : ""}
            </MDSText>

            <MigrationComparison aria-label="Plan price comparison">
              <MigrationPlan>
                <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                  Current plan
                </MDSText>
                <MDSText variant={MDSTextVariant.HeaderS} weight={MDSTextWeight.SemiBold}>
                  {snapshot.currentPlan.displayName}
                </MDSText>
                <MDSText>
                  {preview.currentRecurringAmountCents === null
                    ? formatCurrentPlanPrice(snapshot.currentPlan.monthlyPriceCents)
                    : `${formatCommercialPrice(preview.currentRecurringAmountCents)} / month`}
                </MDSText>
              </MigrationPlan>
              <MigrationArrow aria-hidden="true">→</MigrationArrow>
              <MigrationPlan>
                <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                  New plan
                </MDSText>
                <MDSText variant={MDSTextVariant.HeaderS} weight={MDSTextWeight.SemiBold}>
                  {selectedPlan.displayName}
                </MDSText>
                <MDSText>
                  {formatCommercialPrice(preview.targetRecurringAmountCents)} / month
                </MDSText>
                {preview.targetPaidExtraUsageBlocks > 0 ? (
                  <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                    Includes {preview.targetPaidExtraUsageBlocks} paid Extra Usage{" "}
                    {preview.targetPaidExtraUsageBlocks === 1 ? "block" : "blocks"}
                  </MDSText>
                ) : null}
              </MigrationPlan>
            </MigrationComparison>

            {preview.amountDueNowCents > 0 || preview.endsTrial ? (
              <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
                Due now: {formatCommercialPrice(preview.amountDueNowCents)}
              </MDSText>
            ) : null}

            <AllowanceComparison aria-label="All plan allowances">
              <thead>
                <tr>
                  <th>Allowance</th>
                  <th>Current</th>
                  <th>{selectedPlan.displayName}</th>
                </tr>
              </thead>
              <tbody>
                {preview.allowanceComparisons.map(comparison => (
                  <tr key={comparison.meterKey}>
                    <td>{getMeterLabel(comparison.meterKey)}</td>
                    <td>{formatCurrentAllowance(comparison)}</td>
                    <td>
                      {formatAllowance({
                        key: comparison.meterKey,
                        value: comparison.targetAmount,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </AllowanceComparison>

            {requiresOneWayAcknowledgement ? (
              <MDSCheckbox isSelected={acknowledged} onChange={setAcknowledged}>
                I understand this is a one-way move out of my legacy plan.
              </MDSCheckbox>
            ) : null}
          </>
        ) : null}

        {!isLoadingPreview && !errorMessage && !previewIsComplete ? (
          <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
            A complete current comparison of all eight allowances is required before this change can
            be confirmed. Your current plan and access remain unchanged.
          </MDSText>
        ) : null}
      </MigrationDialogContent>
      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton onClick={closeDialog}>Keep current plan</MDSButton>
        <MDSButton
          variant={MDSButtonVariant.Emphasis}
          isDisabled={!canApply || isApplying}
          isLoading={isApplying}
          onClick={onApply}
        >
          {preview ? getPlanChangeActionLabel(preview) : "Confirm change"}
        </MDSButton>
      </MDSButtonGroup>
    </MDSModalDialog>
  );
};

export const CommercialPlanManagement = ({
  catalog = null,
  initialSelectedPlanKey = null,
  snapshot,
  isChangingPlan = false,
  onManageBilling,
  onApplyPlanChange,
  onPreviewPlanChange,
  onStartSubscription,
}: CommercialPlanManagementProps) => {
  const validatedCatalog = catalog?.generation === snapshot.catalogGeneration ? catalog : null;
  const focusedSelection = useMemo(
    () =>
      validatedCatalog
        ? getCommercialPlanSelection({
            catalog: validatedCatalog,
            meterKey: snapshot.migration?.limitingMeterKey,
            mode: "focused",
            snapshot,
          })
        : null,
    [snapshot, validatedCatalog]
  );
  const [selectedPlanKey, setSelectedPlanKey] = useState<CommercialPlanKey>(
    () =>
      getCatalogPlan(validatedCatalog, initialSelectedPlanKey)?.key ??
      focusedSelection?.initialSelectedPlanKey ??
      CommercialPlanKey.MemPro
  );
  const [selectedPaidExtraUsageBlocks, setSelectedPaidExtraUsageBlocks] = useState(() =>
    getInitialPaidExtraUsageBlocks(snapshot)
  );
  const [allPlansModalIsOpen, setAllPlansModalIsOpen] = useState(false);
  const [planChangeDialogIsOpen, setPlanChangeDialogIsOpen] = useState(false);
  const [planChangePreview, setPlanChangePreview] = useState<CommercialPlanChangePreview | null>(
    null
  );
  const [planChangeError, setPlanChangeError] = useState<string | null>(null);
  const [planChangeIsLoadingPreview, setPlanChangeIsLoadingPreview] = useState(false);
  const [planChangeIsApplying, setPlanChangeIsApplying] = useState(false);
  const [planChangeIdempotencyKey, setPlanChangeIdempotencyKey] = useState<string | null>(null);
  const [reviewedPlanKey, setReviewedPlanKey] = useState<CommercialPlanKey | null>(null);
  const [planChangeOutcome, setPlanChangeOutcome] = useState<CommercialPlanChangeResult | null>(
    null
  );
  const planChangeAttemptRef = useRef<string | null>(null);
  const selectedPlan = useMemo(() => {
    const plan = getCatalogPlan(validatedCatalog, selectedPlanKey);
    if (snapshot.isTrial) {
      const currentPlan = getCatalogPlan(validatedCatalog, snapshot.currentPlan.key);
      if (plan && currentPlan && plan.position > currentPlan.position) {
        return plan;
      }

      return focusedSelection?.visiblePlans[0];
    }

    return plan ?? validatedCatalog?.plans[0];
  }, [validatedCatalog, selectedPlanKey, snapshot, focusedSelection]);
  const focusedPlans = useMemo(() => {
    if (!focusedSelection || !selectedPlan) {
      return focusedSelection?.visiblePlans ?? [];
    }
    if (focusedSelection.visiblePlans.some(plan => plan.key === selectedPlan.key)) {
      return focusedSelection.visiblePlans;
    }

    return [...focusedSelection.visiblePlans.slice(0, -1), selectedPlan];
  }, [focusedSelection, selectedPlan]);
  const reviewedPlan = getCatalogPlan(validatedCatalog, reviewedPlanKey);
  const currentPaidExtraUsageBlocks = snapshot.blockPolicy?.paidBlocks ?? 0;
  const selectedPlanSupportsPaidBlocks = selectedPlan?.supportsPaidExtraUsage === true;
  const targetPaidExtraUsageBlocks = selectedPlanSupportsPaidBlocks
    ? selectedPaidExtraUsageBlocks
    : 0;
  const currentPlanIsFree =
    !snapshot.currentPlan.isLegacy && snapshot.currentPlan.key === CommercialPlanKey.Mem;
  const legacyMigrationIsEligible =
    !snapshot.currentPlan.isLegacy || snapshot.migration?.eligible === true;
  const planIsAvailable = (plan: CommercialPlanDefinition): boolean => {
    const currentPlan = getCatalogPlan(validatedCatalog, snapshot.currentPlan.key);
    if (snapshot.isTrial && (!currentPlan || plan.position <= currentPlan.position)) {
      return false;
    }

    return currentPlanIsFree
      ? plan.key === CommercialPlanKey.Mem || plan.checkoutAvailable
      : validatedCatalog?.planManagementAvailable === true &&
          legacyMigrationIsEligible &&
          (plan.key === CommercialPlanKey.Mem || plan.checkoutAvailable);
  };
  const recommendation = focusedSelection?.recommendedPlanKey;
  const limitingMeterKey = snapshot.migration?.limitingMeterKey;
  const pendingPlan = getCatalogPlan(validatedCatalog, snapshot.pendingChange?.destinationPlanKey);
  const pausedTaskCount = snapshot.pausedWorkingMemoryItems.filter(
    item => item.kind === "assistant_task"
  ).length;
  const pausedProjectCount = snapshot.pausedWorkingMemoryItems.filter(
    item => item.kind === "assistant_project"
  ).length;
  const pausedWorkingMemoryCount = pausedTaskCount + pausedProjectCount;
  const canSelectPaidBlocks =
    selectedPlan?.key === CommercialPlanKey.MemPro20x &&
    validatedCatalog?.extraUsageAvailable === true &&
    validatedCatalog?.planManagementAvailable === true;

  const selectPlan = (plan: CommercialPlanDefinition) => {
    setSelectedPlanKey(plan.key);
    setSelectedPaidExtraUsageBlocks(
      plan.key === snapshot.currentPlan.key && plan.supportsPaidExtraUsage
        ? currentPaidExtraUsageBlocks
        : snapshot.currentPlan.isLegacy &&
            plan.key === snapshot.migration?.recommendedPlanKey &&
            plan.key === CommercialPlanKey.MemPro20x
          ? (snapshot.migration.recommendedPaidExtraUsageBlocks ?? 0)
          : 0
    );
  };

  const openPlanChangeReview = async ({
    paidExtraUsageBlocks,
    plan,
  }: {
    paidExtraUsageBlocks: number;
    plan: CommercialPlanDefinition;
  }) => {
    if (!onPreviewPlanChange || !onApplyPlanChange) {
      return;
    }

    const planChangeAttempt = uuidModule.generate();
    planChangeAttemptRef.current = planChangeAttempt;
    setReviewedPlanKey(plan.key);
    setPlanChangeDialogIsOpen(true);
    setPlanChangePreview(null);
    setPlanChangeError(null);
    setPlanChangeOutcome(null);
    setPlanChangeIdempotencyKey(planChangeAttempt);
    setPlanChangeIsLoadingPreview(true);
    try {
      const preview = await onPreviewPlanChange({
        paidExtraUsageBlocks,
        planKey: plan.key,
      });
      if (snapshot.isTrial && !preview.endsTrial) {
        throw new Error("Your trial status changed. Refresh your plan details before upgrading.");
      }
      if (planChangeAttemptRef.current !== planChangeAttempt) {
        return;
      }

      setPlanChangePreview(preview);
    } catch (error) {
      if (planChangeAttemptRef.current !== planChangeAttempt) {
        return;
      }

      setPlanChangeError(
        error instanceof Error
          ? error.message
          : "Mem could not load this plan change. Your current plan was not changed."
      );
    } finally {
      if (planChangeAttemptRef.current === planChangeAttempt) {
        setPlanChangeIsLoadingPreview(false);
      }
    }
  };

  const applyPlanChange = async () => {
    if (!planChangePreview || !planChangeIdempotencyKey || !onApplyPlanChange) {
      return;
    }

    setPlanChangeError(null);
    setPlanChangeIsApplying(true);
    try {
      const result = await onApplyPlanChange({
        idempotencyKey: planChangeIdempotencyKey,
        preview: planChangePreview,
      });
      setPlanChangeOutcome(result);
      setPlanChangeDialogIsOpen(false);
      planChangeAttemptRef.current = null;
    } catch (error) {
      setPlanChangeError(
        error instanceof Error
          ? error.message
          : "Mem could not confirm this plan change. Your current plan was not changed."
      );
    } finally {
      setPlanChangeIsApplying(false);
    }
  };

  const choosePlan = (plan: CommercialPlanDefinition) => {
    if (!planIsAvailable(plan)) {
      return;
    }

    const paidExtraUsageBlocks =
      plan.key === selectedPlan?.key && plan.supportsPaidExtraUsage
        ? targetPaidExtraUsageBlocks
        : 0;

    if (!currentPlanIsFree) {
      void openPlanChangeReview({ paidExtraUsageBlocks, plan });
      return;
    }

    onStartSubscription?.({
      paidExtraUsageBlocks,
      planKey: plan.key,
    });
  };

  return (
    <CommercialPlanSurface>
      <CurrentPlanHeader>
        <div>
          <HeaderLine>
            <MDSText variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
              {snapshot.currentPlan.displayName}
            </MDSText>
            <MDSBadge
              label={snapshot.currentPlan.isLegacy ? "Legacy plan" : "Current plan"}
              variant={MDSBadgeVariant.Default}
            />
          </HeaderLine>
          <MDSText color={MDSTextColor.Secondary}>
            {formatCurrentPlanPrice(snapshot.currentPlan.monthlyPriceCents)}
          </MDSText>
        </div>
        {onManageBilling ? <MDSButton onClick={onManageBilling}>Manage billing</MDSButton> : null}
      </CurrentPlanHeader>

      {snapshot.currentPlan.isLegacy ? (
        <LegacyNotice>
          <MDSText variant={MDSTextVariant.TextS}>
            Your price and legacy allowances stay unchanged until you choose a new Mem plan. Moving
            to the new catalog
            {snapshot.migration?.canReturn === false
              ? " is one-way."
              : " requires a confirmed migration preview."}
          </MDSText>
        </LegacyNotice>
      ) : null}

      {pendingPlan && snapshot.pendingChange ? (
        <PendingChangeNotice role="status">
          <MDSText variant={MDSTextVariant.TextS}>
            {snapshot.pendingChange.status === CommercialPendingPlanChangeStatus.PendingPayment ? (
              <>
                Stripe is still confirming payment for your switch to {pendingPlan.displayName}
                {snapshot.pendingChange.paidExtraUsageBlocks === null
                  ? ""
                  : ` with ${snapshot.pendingChange.paidExtraUsageBlocks} paid Extra Usage ${snapshot.pendingChange.paidExtraUsageBlocks === 1 ? "block" : "blocks"}`}
                . Your current plan remains in effect until payment succeeds.
              </>
            ) : (
              <>
                Your switch to {pendingPlan.displayName}
                {snapshot.pendingChange.paidExtraUsageBlocks === null
                  ? ""
                  : ` with ${snapshot.pendingChange.paidExtraUsageBlocks} paid Extra Usage ${snapshot.pendingChange.paidExtraUsageBlocks === 1 ? "block" : "blocks"}`}{" "}
                is scheduled for {formatDate(snapshot.pendingChange.effectiveAt)}.
              </>
            )}
          </MDSText>
        </PendingChangeNotice>
      ) : snapshot.pendingChange?.paidExtraUsageBlocks !== null && snapshot.pendingChange ? (
        <PendingChangeNotice role="status">
          <MDSText variant={MDSTextVariant.TextS}>
            {snapshot.pendingChange.status === CommercialPendingPlanChangeStatus.PendingPayment ? (
              <>
                Stripe is still confirming payment for your change to{" "}
                {snapshot.pendingChange.paidExtraUsageBlocks} paid Extra Usage{" "}
                {snapshot.pendingChange.paidExtraUsageBlocks === 1 ? "block" : "blocks"}. Your
                current Extra Usage allowance remains in effect until payment succeeds.
              </>
            ) : (
              <>
                Your change to {snapshot.pendingChange.paidExtraUsageBlocks} paid Extra Usage{" "}
                {snapshot.pendingChange.paidExtraUsageBlocks === 1 ? "block" : "blocks"} is
                scheduled for {formatDate(snapshot.pendingChange.effectiveAt)}.
              </>
            )}
          </MDSText>
        </PendingChangeNotice>
      ) : null}

      {planChangeOutcome?.status === CommercialPlanChangeStatus.PendingPayment ? (
        <PendingChangeNotice role="status">
          <MDSText variant={MDSTextVariant.TextS}>
            Stripe is still confirming payment for your change to{" "}
            {getCatalogPlan(validatedCatalog, planChangeOutcome.preview.targetPlanKey)
              ?.displayName ?? "the selected plan"}
            . Your current plan remains in effect until payment succeeds.
          </MDSText>
        </PendingChangeNotice>
      ) : planChangeOutcome?.status === CommercialPlanChangeStatus.Scheduled ? (
        <PendingChangeNotice role="status">
          <MDSText variant={MDSTextVariant.TextS}>
            Your plan change is scheduled for {formatDate(planChangeOutcome.preview.effectiveAt)}.
          </MDSText>
        </PendingChangeNotice>
      ) : planChangeOutcome?.status === CommercialPlanChangeStatus.Applied ? (
        <PendingChangeNotice role="status">
          <MDSText variant={MDSTextVariant.TextS}>
            Stripe confirmed your plan change. The usage summary above reflects the latest
            authoritative state.
          </MDSText>
        </PendingChangeNotice>
      ) : planChangeOutcome?.status === CommercialPlanChangeStatus.Unchanged ? (
        <PendingChangeNotice role="status">
          <MDSText variant={MDSTextVariant.TextS}>
            No billing change was needed. The usage summary above reflects your current plan.
          </MDSText>
        </PendingChangeNotice>
      ) : null}

      <Section>
        <SectionHeading>
          <div>
            <MDSText variant={MDSTextVariant.HeaderS} weight={MDSTextWeight.SemiBold}>
              Usage this billing period
            </MDSText>
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
              Counters reset on {formatDate(snapshot.period.endsAt)}. Working-memory meters also
              show their period peak.
            </MDSText>
          </div>
          {snapshot.blockPolicy ? (
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
              {snapshot.blockPolicy.paidBlocks + snapshot.blockPolicy.complimentaryBlocks} extra{" "}
              {snapshot.blockPolicy.paidBlocks + snapshot.blockPolicy.complimentaryBlocks === 1
                ? "block"
                : "blocks"}
            </MDSText>
          ) : null}
        </SectionHeading>
        <CommercialUsageProgress snapshot={snapshot} />
        {pausedWorkingMemoryCount > 0 ? (
          <PendingChangeNotice role="status">
            <MDSText variant={MDSTextVariant.TextS}>
              Mem Agent monitoring is paused for {pausedWorkingMemoryCount} working-memory{" "}
              {pausedWorkingMemoryCount === 1 ? "item" : "items"} ({pausedTaskCount}{" "}
              {pausedTaskCount === 1 ? "task" : "tasks"}, {pausedProjectCount}{" "}
              {pausedProjectCount === 1 ? "project" : "projects"}). Their content is safe. Complete
              unneeded items or choose more capacity below; monitoring resumes automatically when
              capacity is available.
            </MDSText>
          </PendingChangeNotice>
        ) : null}
      </Section>

      {validatedCatalog && selectedPlan ? (
        <>
          <Section>
            <SectionHeading>
              <div>
                <MDSText variant={MDSTextVariant.HeaderS} weight={MDSTextWeight.SemiBold}>
                  Compare plans
                </MDSText>
                <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                  Every plan includes notes, Agent requests, Voice Mode, and bounded asynchronous
                  memory work.
                </MDSText>
                {recommendation && limitingMeterKey ? (
                  <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                    Your recommendation is based on {getMeterLabel(limitingMeterKey).toLowerCase()}.
                  </MDSText>
                ) : null}
              </div>
            </SectionHeading>

            {focusedSelection ? (
              <CommercialPlanSelector
                actionIsLoading={isChangingPlan || planChangeIsLoadingPreview}
                allowCurrentPlanAction={currentPaidExtraUsageBlocks !== targetPaidExtraUsageBlocks}
                currentPlanActionLabel="Review Extra Usage change"
                currentPlanKey={snapshot.currentPlan.key}
                getActionLabel={plan =>
                  currentPlanIsFree
                    ? `Choose ${plan.displayName}`
                    : `Review switch to ${plan.displayName}`
                }
                isPlanAvailable={planIsAvailable}
                memProTrialIsAvailable={snapshot.memProTrialEligible === true}
                onChoosePlan={choosePlan}
                onSelectPlan={planKey => {
                  const plan = getCatalogPlan(validatedCatalog, planKey);
                  if (plan) {
                    selectPlan(plan);
                  }
                }}
                onShowAllPlans={() => setAllPlansModalIsOpen(true)}
                plans={focusedPlans}
                recommendedPlanKey={focusedSelection.recommendedPlanKey}
                selectedPlanKey={selectedPlan.key}
              />
            ) : null}

            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
              Notes remain stored and searchable on every plan. Active-note and working-memory
              limits control what Mem Agent proactively processes and monitors.
            </MDSText>
          </Section>

          {selectedPlan.supportsPaidExtraUsage ? (
            <ExtraUsageDetails
              block={validatedCatalog.extraUsageBlock}
              canSelectBlocks={canSelectPaidBlocks}
              onDecrement={() =>
                setSelectedPaidExtraUsageBlocks(current => Math.max(0, current - 1))
              }
              onIncrement={() => setSelectedPaidExtraUsageBlocks(current => current + 1)}
              selectedPaidBlocks={targetPaidExtraUsageBlocks}
            />
          ) : null}

          <CommercialPlanSelectorModal
            actionIsLoading={isChangingPlan || planChangeIsLoadingPreview}
            catalog={validatedCatalog}
            getActionLabel={plan =>
              currentPlanIsFree
                ? `Choose ${plan.displayName}`
                : `Review switch to ${plan.displayName}`
            }
            isPlanAvailable={planIsAvailable}
            isOpen={allPlansModalIsOpen}
            mode="all"
            onChoosePlan={plan => {
              setAllPlansModalIsOpen(false);
              selectPlan(plan);
              choosePlan(plan);
            }}
            onClose={() => setAllPlansModalIsOpen(false)}
            onSelectedPlanKeyChange={planKey => {
              const plan = getCatalogPlan(validatedCatalog, planKey);
              if (plan) {
                selectPlan(plan);
              }
            }}
            snapshot={snapshot}
            title="Compare Mem plans"
          />

          {reviewedPlan ? (
            <PlanChangeDialog
              key={planChangeIdempotencyKey}
              errorMessage={planChangeError}
              isApplying={planChangeIsApplying}
              isLoadingPreview={planChangeIsLoadingPreview}
              isOpen={planChangeDialogIsOpen}
              onApply={onApplyPlanChange ? () => void applyPlanChange() : undefined}
              onClose={() => {
                planChangeAttemptRef.current = null;
                setPlanChangeIsLoadingPreview(false);
                setPlanChangeDialogIsOpen(false);
              }}
              preview={planChangePreview}
              selectedPlan={reviewedPlan}
              snapshot={snapshot}
            />
          ) : null}
        </>
      ) : (
        <CatalogUnavailableNotice role="status">
          <MDSText variant={MDSTextVariant.TextS}>
            Plan comparison is temporarily unavailable. Your current plan and usage are shown above,
            and no billing changes can be made from this screen.
          </MDSText>
        </CatalogUnavailableNotice>
      )}
    </CommercialPlanSurface>
  );
};

const CommercialPlanSurface = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[8]};
  width: 100%;
  max-width: 960px;
  padding-bottom: ${({ theme }) => theme.spacing[8]};
`;

const CurrentPlanHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const HeaderLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const LegacyNotice = styled.div`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.background.banner.default};
`;

const PendingChangeNotice = styled(LegacyNotice)`
  background: ${({ theme }) => theme.colors.background.banner.emphasis};
`;

const CatalogUnavailableNotice = styled(LegacyNotice)`
  background: ${({ theme }) => theme.colors.background.banner.default};
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SectionHeading = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ExtraUsagePanel = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[5]};
  border: 1px solid ${({ theme }) => theme.colors.border.emphasis};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.background.card.emphasis};
`;

const ExtraUsageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ExtraUsageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
`;

const ExtraUsageSelector = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
  padding-top: ${({ theme }) => theme.spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const ExtraUsageBlockCount = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  font-variant-numeric: tabular-nums;
`;

const MigrationDialogContent = styled(MDSDialogTextContent)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const PlanChangeError = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.background.banner.default};
`;

const MigrationComparison = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};

  @container mds-panel (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const MigrationPlan = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.background.card.default};
`;

const MigrationArrow = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};

  @container mds-panel (max-width: 520px) {
    transform: rotate(90deg);
    justify-self: center;
  }
`;

const AllowanceComparison = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};

  th,
  td {
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
    text-align: left;
  }

  th {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
  }
`;
